import React, { useMemo } from "react";

import Chart, { ChartData, ChartOptions } from "veneer/Chart";

import { utcToLocal } from "cortex/ui/src/util/time";

type LegendAlignment = "center" | "end" | "start";

interface ResourceLineChartProps {
  data: ChartData<"line">;
  formatTooltipValue: (label: string, value: number) => string;
  formatYAxisTick?: (value: string | number) => string;
  legendAlign?: LegendAlignment;
  timestamps: ReadonlyArray<number>;
  yAxisMax?: number;
  yAxisMin?: number;
  yAxisStepSize?: number;
  yAxisTitle?: string;
}

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
const TWO_HOURS_IN_MS = 2 * 60 * 60 * 1000;
const TWELVE_HOURS_IN_MS = 12 * 60 * 60 * 1000;
const TWO_DAYS_IN_MS = 2 * 24 * 60 * 60 * 1000;
const SIX_HOURS_IN_MS = 6 * 60 * 60 * 1000;

function formatXAxisTick(timestamp: number): string {
  const date = new Date(timestamp);
  if (date.getHours() === 0 && date.getMinutes() === 0) {
    const month = date.toLocaleString("en-GB", { month: "short" });
    return `${date.getDate()}. ${month}`;
  }

  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
  });
}

function getXAxisStepSize(timestamps: ReadonlyArray<number>): number {
  if (timestamps.length < 2) {
    return FIVE_MINUTES_IN_MS;
  }

  const range = Math.max(...timestamps) - Math.min(...timestamps);
  if (range <= SIX_HOURS_IN_MS) {
    return FIVE_MINUTES_IN_MS;
  }

  if (range <= TWO_DAYS_IN_MS) {
    return TWO_HOURS_IN_MS;
  }

  return TWELVE_HOURS_IN_MS;
}

function roundToLocalInterval(
  timestamp: number,
  interval: number,
  round: (value: number) => number,
): number {
  const timezoneOffset = new Date(timestamp).getTimezoneOffset() * 60 * 1000;
  const localTimestamp = timestamp - timezoneOffset;
  return round(localTimestamp / interval) * interval + timezoneOffset;
}

function getXAxisBounds(
  timestamps: ReadonlyArray<number>,
  stepSize: number,
): { max: number; min: number } | undefined {
  if (timestamps.length === 0) {
    return undefined;
  }

  return {
    max: roundToLocalInterval(Math.max(...timestamps), stepSize, Math.ceil),
    min: roundToLocalInterval(Math.min(...timestamps), stepSize, Math.floor),
  };
}

export default function ResourceLineChart({
  data,
  formatTooltipValue,
  formatYAxisTick,
  legendAlign = "center",
  timestamps,
  yAxisMax,
  yAxisMin,
  yAxisStepSize = 1,
  yAxisTitle,
}: ResourceLineChartProps) {
  const options = useMemo<ChartOptions<"line">>(() => {
    const xAxisStepSize = getXAxisStepSize(timestamps);
    const xAxisBounds = getXAxisBounds(timestamps, xAxisStepSize);

    return {
      animation: false,
      interaction: {
        intersect: false,
        mode: "index",
      },
      maintainAspectRatio: false,
      parsing: false,
      plugins: {
        legend: {
          align: legendAlign,
          display: true,
          position: "bottom",
        },
        tooltip: {
          callbacks: {
            label: (item) => {
              const value = item.parsed.y;
              if (value === null) {
                return "";
              }

              return formatTooltipValue(item.dataset.label ?? "", value);
            },
            title: (items) => {
              const timestamp = items[0]?.parsed.x;
              return timestamp === undefined || timestamp === null
                ? ""
                : utcToLocal(timestamp);
            },
          },
        },
      },
      scales: {
        x: {
          max: xAxisBounds?.max,
          min: xAxisBounds?.min,
          type: "linear",
          ticks: {
            autoSkip: true,
            callback: (value) => formatXAxisTick(Number(value)),
            stepSize: xAxisStepSize,
          },
        },
        y: {
          max: yAxisMax,
          min: yAxisMin,
          offset: true,
          ticks: {
            callback: formatYAxisTick
              ? (value) => formatYAxisTick(value)
              : undefined,
            precision: 0,
            stepSize: yAxisStepSize,
          },
          title: {
            display: Boolean(yAxisTitle),
            text: yAxisTitle,
          },
        },
      },
    };
  }, [
    formatTooltipValue,
    formatYAxisTick,
    legendAlign,
    timestamps,
    yAxisMax,
    yAxisMin,
    yAxisStepSize,
    yAxisTitle,
  ]);

  return (
    <Chart data={data} height={300} options={options} showLegend type="line" />
  );
}
